import React, { useState, useEffect, useRef, useCallback } from 'react';
import { database } from '../firebaseConfig';
import { ref, get, update } from 'firebase/database';
import { costOf } from '../config/creditPlans';
import {
  GOOGLE_REVIEW_SETTINGS_PATH,
  REVIEW_FOCUS_OPTIONS,
  REVIEW_DEPTH_OPTIONS,
  REVIEW_LANGUAGE_OPTIONS,
  CUSTOM_FOCUS_PRESETS,
  MAX_CUSTOM_FOCUS,
  MAX_OFFER_CONTEXT,
  normalizeReviewSettings,
} from './googleReviewAudit';
import './LeadSpySettings.css';

const DEBOUNCE_MS = 800;
const AUDIT_CREDIT_COST = costOf('googleReviewAudit');

const GoogleReviewAuditSettings = ({ userId }) => {
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [settings, setSettings] = useState(() => normalizeReviewSettings(null));

  const debounceRef = useRef(null);
  const savedTimerRef = useRef(null);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    if (!userId) return undefined;
    setLoading(true);
    hasLoadedRef.current = false;
    const done = () => {
      setLoading(false);
      requestAnimationFrame(() => { hasLoadedRef.current = true; });
    };
    get(ref(database, GOOGLE_REVIEW_SETTINGS_PATH(userId)))
      .then((snapshot) => {
        setSettings(normalizeReviewSettings(snapshot.exists() ? snapshot.val() : null));
        done();
      })
      .catch(done);
    return undefined;
  }, [userId]);

  const persist = useCallback(async (next) => {
    if (!userId) return;
    setSaveStatus('saving');
    try {
      await update(ref(database, GOOGLE_REVIEW_SETTINGS_PATH(userId)), {
        ...normalizeReviewSettings(next),
        updatedAt: Date.now(),
      });
      setSaveStatus('saved');
      clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 2400);
    } catch (error) {
      console.error('Error saving Google Review Audit settings:', error);
      setSaveStatus('idle');
    }
  }, [userId]);

  useEffect(() => {
    if (!hasLoadedRef.current) return undefined;
    clearTimeout(debounceRef.current);
    setSaveStatus('idle');
    debounceRef.current = setTimeout(() => persist(settings), DEBOUNCE_MS);
    return () => clearTimeout(debounceRef.current);
  }, [settings, persist]);

  useEffect(() => () => {
    clearTimeout(debounceRef.current);
    clearTimeout(savedTimerRef.current);
  }, []);

  const set = (key, value) => setSettings((prev) => ({ ...prev, [key]: value }));

  // At least one lens stays on — an audit that extracts nothing is just a cost.
  const toggleFocus = (id) => setSettings((prev) => {
    const has = prev.focus.includes(id);
    if (has && prev.focus.length === 1) return prev;
    return { ...prev, focus: has ? prev.focus.filter((f) => f !== id) : [...prev.focus, id] };
  });

  const addPreset = (text) => setSettings((prev) => {
    if (prev.customFocus.includes(text)) return prev;
    const next = prev.customFocus ? `${prev.customFocus.trim()} ${text}` : text;
    return { ...prev, customFocus: next.slice(0, MAX_CUSTOM_FOCUS) };
  });

  if (loading) {
    return (
      <div className="spySettingsLoading">
        <i className="fa fa-spinner fa-spin"></i>
      </div>
    );
  }

  return (
    <div className="igResearchSettings">
      <div className="advancedCard">
        <div className="advancedCardHeader">
          <div className="advancedCardIcon">
            <i className="fa fa-filter"></i>
          </div>
          <div className="advancedCardTitle">
            <h5>What to pull out of the reviews</h5>
            <p>Only the selected parts are analysed and saved on the contact</p>
          </div>
        </div>
        <div className="advancedCardBody">
          <div className="socialPlatformsGrid">
            {REVIEW_FOCUS_OPTIONS.map((opt) => {
              const active = settings.focus.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  title={opt.hint}
                  className={`socialPlatformBtn ${active ? 'active' : ''}`}
                  onClick={() => toggleFocus(opt.id)}
                >
                  <i className={`fa ${opt.icon}`}></i>
                  <span>{opt.label}</span>
                  {active && <i className="fa fa-check checkIcon"></i>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="advancedCard">
        <div className="advancedCardHeader">
          <div className="advancedCardIcon">
            <i className="fa fa-crosshairs"></i>
          </div>
          <div className="advancedCardTitle">
            <h5>Custom focus</h5>
            <p>Anything specific to look for. The audit answers it explicitly, including “not mentioned”.</p>
          </div>
        </div>
        <div className="advancedCardBody">
          <div className="textareaWrapper">
            <div className="textareaGutter">
              <span className="gutterIcon"><i className="fa fa-magnifying-glass"></i></span>
            </div>
            <textarea
              className="customContextInput"
              placeholder="e.g. How do customers rate waiting times and reachability by phone?"
              value={settings.customFocus}
              maxLength={MAX_CUSTOM_FOCUS}
              onChange={(e) => set('customFocus', e.target.value)}
            />
          </div>
          <div className="templatesPills" style={{ marginTop: 10 }}>
            {CUSTOM_FOCUS_PRESETS.map((preset) => (
              <button key={preset.label} type="button" className="templatePill" onClick={() => addPreset(preset.text)}>
                {preset.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="advancedCard">
        <div className="advancedCardHeader">
          <div className="advancedCardIcon">
            <i className="fa fa-briefcase"></i>
          </div>
          <div className="advancedCardTitle">
            <h5>What do you sell?</h5>
            <p>Sales angle and openers connect the weak spots to your offer</p>
          </div>
        </div>
        <div className="advancedCardBody">
          <div className="textareaWrapper">
            <div className="textareaGutter">
              <span className="gutterIcon"><i className="fa fa-briefcase"></i></span>
            </div>
            <textarea
              className="customContextInput"
              style={{ minHeight: 64 }}
              placeholder="e.g. AI phone assistant for medical practices that answers calls and books appointments"
              value={settings.offerContext}
              maxLength={MAX_OFFER_CONTEXT}
              onChange={(e) => set('offerContext', e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="advancedCard">
        <div className="advancedCardHeader">
          <div className="advancedCardIcon">
            <i className="fa fa-layer-group"></i>
          </div>
          <div className="advancedCardTitle">
            <h5>Depth</h5>
            <p>{REVIEW_DEPTH_OPTIONS.find((o) => o.id === settings.depth)?.hint} · {AUDIT_CREDIT_COST} credits per contact</p>
          </div>
        </div>
        <div className="advancedCardBody">
          <div className="languageOptions">
            {REVIEW_DEPTH_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={`langOption ${settings.depth === opt.id ? 'active' : ''}`}
                onClick={() => set('depth', opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="advancedCard">
        <div className="advancedCardHeader">
          <div className="advancedCardIcon">
            <i className="fa fa-globe"></i>
          </div>
          <div className="advancedCardTitle">
            <h5>Save language</h5>
            <p>Language of the audit saved on the contact</p>
          </div>
        </div>
        <div className="advancedCardBody">
          <div className="languageOptions">
            {REVIEW_LANGUAGE_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                className={`langOption ${settings.language === opt.id ? 'active' : ''}`}
                onClick={() => set('language', opt.id)}
              >
                {opt.flag} {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={`spyAutoSaveIndicator ${saveStatus}`}>
        {saveStatus === 'saving' && (
          <>
            <span className="autoSaveSpinner"></span>
            <span>Saving…</span>
          </>
        )}
        {saveStatus === 'saved' && (
          <>
            <i className="fa fa-check"></i>
            <span>Saved</span>
          </>
        )}
      </div>
    </div>
  );
};

export default GoogleReviewAuditSettings;
